import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Link, Grid, IconButton, useTheme } from '@mui/material';
import { Facebook, Twitter, LinkedIn, Instagram } from '@mui/icons-material';
import axios from 'axios';

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

const Footer = () => {
  const theme = useTheme();
  const year = new Date().getFullYear();
  
  // State for dynamic menu data
  const [menuData, setMenuData] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [menuError, setMenuError] = useState(null);

  // Fetch hierarchical menu data
  useEffect(() => {
    fetchMenuData();
  }, []);

  const fetchMenuData = async () => {
    try {
      setMenuLoading(true);
      setMenuError(null);
      const response = await axios.get(`${apiUrl}/menus`);
      setMenuData(response.data);
    } catch (err) {
      console.error("Error fetching menu data:", err);
      setMenuError(err.message || "Failed to fetch menu data");
      // Fallback to default menu items if API fails
      setMenuData([
        { id: 1, name: "Home", url: "/", children: [] },
        { id: 2, name: "Articles", url: "/articles", children: [] },
        { id: 3, name: "About", url: "/about", children: [] },
        { id: 4, name: "Contact", url: "/contact", children: [] },
        { id: 5, name: "Privacy Policy", url: "/privacy", children: [] },
        { id: 6, name: "Terms of Service", url: "/terms", children: [] },
      ]);
    } finally {
      setMenuLoading(false);
    }
  };

  // Apply menu item styling from database
  const getMenuItemStyle = (menuItem) => ({
    fontFamily: menuItem.font_family || "inherit",
    fontSize: menuItem.font_size || "inherit",
    fontWeight: menuItem.font_weight || "inherit",
    color: menuItem.color || "text.secondary",
    fontStyle: menuItem.style || "inherit",
    textDecoration: menuItem.style === "underline" ? "underline" : "none",
  });

  // Filter menu items for footer - only show important/main items
  const getFilteredMenuItems = () => {
    if (!menuData || menuData.length === 0) return { main: [], secondary: [] };
    
    // Define which menu items should appear in footer
    const importantMenuNames = [
      'home', 'articles', 'about', 'contact', 'news', 'blog', 
      'finance', 'business', 'markets', 'economy', 'technology'
    ];
    
    const legalMenuNames = [
      'terms'
    ];

    // Don't filter main navigation items anymore (Quick Links section removed)
    const mainItems = [];

    // Filter legal/secondary items - only Terms of Service
    const secondaryItems = menuData.filter(item => {
      const itemName = item.name.toLowerCase();
      return legalMenuNames.some(legal => itemName.includes(legal));
    }).slice(0, 1); // Only show 1 item: Terms of Service

    return { main: mainItems, secondary: secondaryItems };
  };

  // Render clean menu item (no children, just main links)
  const renderCleanMenuItem = (menuItem) => {
    const menuStyle = getMenuItemStyle(menuItem);

    return (
      <Link 
        key={menuItem.id}
        href={menuItem.url || "#"} 
        color="text.secondary" 
        sx={{ 
          ...menuStyle,
          display: 'block',
          mb: 1,
          '&:hover': {
            color: 'primary.main',
          }
        }}
      >
        {menuItem.name}
      </Link>
    );
  };

  const { main: mainMenuItems, secondary: secondaryMenuItems } = getFilteredMenuItems();

  // Static fallback menu items if no suitable dynamic items found
  const staticSecondaryItems = [
    { id: 'static-6', name: 'Terms of Service', url: '/terms' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        py: 6,
        px: 2,
        mt: 'auto',
        backgroundColor: theme.palette.mode === 'light' 
          ? 'rgba(232, 236, 241, 0.8)' 
          : 'rgba(0, 0, 0, 0.1)',
        borderTop: `1px solid ${theme.palette.divider}`
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Company Info and Social Media */}
          <Grid item xs={12} sm={4}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Asia Economic Times
            </Typography>
            <Box sx={{ mt: 2 }}>
              <IconButton 
                color="primary" 
                aria-label="facebook"
                component="a"
                href="https://www.facebook.com/share/1AzcXuLxY1/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Facebook />
              </IconButton>
              <IconButton 
                color="primary" 
                aria-label="twitter"
                component="a"
                href="https://x.com/AEconomicTimes"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Twitter />
              </IconButton>
              <IconButton 
                color="primary" 
                aria-label="linkedin"
                component="a"
                href="https://www.linkedin.com/in/asia-economictimes-58388836b/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <LinkedIn />
              </IconButton>
              <IconButton 
                color="primary" 
                aria-label="instagram"
                component="a"
                href="https://www.instagram.com/asiaeconomictimes/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram />
              </IconButton>
            </Box>
          </Grid>

          {/* Legal Links */}
          <Grid item xs={12} sm={4}>
            {/* Legal/Secondary Links */}
            <Typography variant="h6" color="text.primary" gutterBottom>
              Legal
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', mb: 3 }}>
              {menuLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Loading...
                </Typography>
              ) : (
                // Show filtered secondary items or fallback to static
                (secondaryMenuItems.length > 0 ? secondaryMenuItems : staticSecondaryItems)
                  .map((menuItem) => renderCleanMenuItem(menuItem))
              )}
            </Box>

          </Grid>
        </Grid>
        
        {/* Clean Copyright Section */}
        <Box sx={{ mt: 5, pt: 3, borderTop: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            © {year} Asia Economic Times. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;

